import { useState, useEffect } from "react"
import { Head, router, useForm, usePage } from "@inertiajs/react"
import { CoachLayout } from "./components/CoachLayout"
import { SectionTabs } from "./components/SectionTabs"
import { ShareableLinkCard } from "./components/ShareableLinkCard"
import { CopyButton } from "@/components/CopyButton"
import { FunnelStageBadge } from "./components/FunnelStageBadge"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Plus,
  Link2,
  Mail,
  Users,
  Trash2,
} from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

const userManagementTabs = [
  { label: "Users", href: "/app/coach/users" },
  { label: "Groups", href: "/app/coach/group" },
  { label: "Prospects", href: "/app/coach/prospects" },
]

function buildEmailTemplate({ prospectName, coachName, link }) {
  const firstName = prospectName.trim().split(/\s+/)[0] || "friend"
  const assessmentBlurb =
    "I'd love for you to take a short Kingdom Factor assessment before our next call. You'll fill out the Integrated Life Audit and answer a few questions about your business, your goals, and what matters most to you — about 10–15 minutes. At the end you'll get a personalized report on how a Christian business peer group could help you in the areas you care about, and I'll use it to shape our follow-up."

  return `Hi ${firstName},

Thanks again for the conversation — I really enjoyed hearing your story.

${assessmentBlurb}

Here's your private link (it'll sign you in automatically):

${link}

Let me know if anything feels unclear. Talk soon.

— ${coachName}`
}


function InviteDialog({ open, onOpenChange, generated, coachName }) {
  const form = useForm({ name: "", email: "" })

  const submit = (e) => {
    e.preventDefault()
    form.post("/app/coach/intake_invites", {
      preserveScroll: true,
      onSuccess: () => form.reset(),
    })
  }

  // Once the server returns the generated invite, swap the dialog into the
  // "result" view. Closing the dialog clears form state for the next invite.
  const showingResult = !!generated
  const emailBody = generated
    ? buildEmailTemplate({
        prospectName: generated.name,
        coachName: coachName || "Your coach",
        link: generated.link,
      })
    : ""

  // Cancel/Close buttons call this directly rather than the raw onOpenChange
  // prop — that prop bypasses the Dialog's own onOpenChange below (which only
  // fires for Radix-initiated closes like Escape or overlay click), so without
  // this, cancelling a half-filled form would leave that text in place next open.
  const close = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) form.reset(); onOpenChange(next) }}>
      <DialogContent className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        {showingResult ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-kf-red" />
                Invite ready for {generated.name}
              </DialogTitle>
              <DialogDescription>
                Copy the link or the suggested email and send it from your own
                inbox so it lands as a real note from you.
              </DialogDescription>
            </DialogHeader>

            <div className="min-w-0 space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500">
                  Magic link — reusable for 30 days
                </Label>
                <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                  <Input
                    value={generated.link}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    className="min-w-0 font-mono text-xs"
                  />
                  <CopyButton text={generated.link} label="Copy link" />
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Anyone with this link signs in as {generated.email} automatically
                  and can come back to their assessment any time. Send it only to them.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wide text-zinc-500">
                    Suggested email — edit to your voice
                  </Label>
                  <CopyButton text={emailBody} label="Copy email" />
                </div>
                <Textarea
                  value={emailBody}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  rows={12}
                  className="font-mono text-xs"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:justify-between">
              <Button type="button" variant="outline" onClick={close}>
                Close
              </Button>
              <a
                href={`mailto:${encodeURIComponent(generated.email)}?subject=${encodeURIComponent(
                  "Your Kingdom Factor assessment",
                )}&body=${encodeURIComponent(emailBody)}`}
              >
                <Button type="button" variant="gradient">
                  <Mail className="w-4 h-4 mr-1.5" /> Open in email client
                </Button>
              </a>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Invite a prospect</DialogTitle>
              <DialogDescription>
                Generate a personal assessment link, then send it from your own
                inbox so it lands as a real note from you — not a system message.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="prospect-name">Prospect's full name</Label>
                <Input
                  id="prospect-name"
                  type="text"
                  value={form.data.name}
                  onChange={(e) => form.setData("name", e.target.value)}
                  placeholder="Jane Doe"
                  required
                />
                {form.errors.name && (
                  <p className="text-sm text-red-600 mt-1">{form.errors.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="prospect-email">Prospect's email</Label>
                <Input
                  id="prospect-email"
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="jane@example.com"
                  required
                />
                {form.errors.email && (
                  <p className="text-sm text-red-600 mt-1">{form.errors.email}</p>
                )}
              </div>

              <p className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
                They'll receive the peer-group assessment — the Integrated Life
                Wheel plus 10 questions (about 10–15 minutes), ending in a
                personalized report on how a peer group could help them.
              </p>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" variant="gradient" disabled={form.processing}>
                  {form.processing ? "Generating…" : "Generate invite link"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function formatDate(iso) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString("en-CA")
  } catch {
    return null
  }
}

// "2 hours ago" / "3 days ago" / "just now". Falls back to a date for
// anything older than a week so the page doesn't say "104 days ago".
function formatRelative(iso) {
  if (!iso) return null
  try {
    const then = new Date(iso).getTime()
    const diffMs = Date.now() - then
    const minutes = Math.round(diffMs / 60_000)
    if (minutes < 1) return "just now"
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.round(hours / 24)
    if (days < 7) return `${days}d ago`
    return formatDate(iso)
  } catch {
    return null
  }
}

export default function Prospects({ currentUser, prospects, coachDisplayName, coachRegistrationUrl }) {
  const { flash } = usePage().props
  const generated = flash?.generated_invite
  const [inviteOpen, setInviteOpen] = useState(false)
  const [result, setResult] = useState(null)

  // When the create action redirects back with `flash.generated_invite`,
  // copy it into local state and re-open the dialog into result mode so the
  // coach can copy the link. Mirroring into state (rather than reading the
  // flash prop directly) lets a fresh "Invite a prospect" click clear it —
  // Inertia keeps `flash` resident for the SPA page's lifetime, so the raw
  // prop alone would still look truthy on the next open.
  useEffect(() => {
    if (generated) {
      setResult(generated)
      setInviteOpen(true)
    }
  }, [generated])

  const openFreshInvite = () => {
    setResult(null)
    setInviteOpen(true)
  }

  // Multi-select for bulk removal. `pendingRemoval` holds the prospect
  // object(s) awaiting confirmation (null = dialog closed); the same dialog
  // backs both the per-row trash icon and the "Remove selected" bar.
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [pendingRemoval, setPendingRemoval] = useState(null)

  const visibleTabs = userManagementTabs

  const openProspect = (p) => {
    router.visit(`/app/coach/members/${p.id}?back=prospects`)
  }

  const allSelected =
    prospects.length > 0 && prospects.every((p) => selectedIds.has(p.id))

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allSelected) prospects.forEach((p) => next.delete(p.id))
      else prospects.forEach((p) => next.add(p.id))
      return next
    })
  }

  const confirmRemoval = () => {
    if (!pendingRemoval?.length) return
    const ids = pendingRemoval.map((p) => p.id)
    router.delete("/app/coach/members/bulk_destroy", {
      data: { ids },
      preserveScroll: true,
      onSuccess: () => setSelectedIds(new Set()),
      onFinish: () => setPendingRemoval(null),
    })
  }

  const removeCount = pendingRemoval?.length ?? 0

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Prospects - Kingdom Factor" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Prospects</h1>
            <p className="text-zinc-600 mt-1">
              Create a prospect and send them the Kingdom Factor assessment.
            </p>
          </div>
          <Button variant="gradient" onClick={openFreshInvite}>
            <Plus className="h-4 w-4 mr-1.5" />
            Invite a prospect
          </Button>
        </div>

        <SectionTabs tabs={visibleTabs} />

        <ShareableLinkCard
          icon={Link2}
          title="Your registration link"
          description="Share this link and prospects can register for the assessment themselves — no invite needed."
          url={coachRegistrationUrl}
        />

        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-2.5">
            <span className="text-sm font-medium text-red-900">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear
              </Button>
              <Button
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() =>
                  setPendingRemoval(prospects.filter((p) => selectedIds.has(p.id)))
                }
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Remove selected
              </Button>
            </div>
          </div>
        )}

        {prospects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-zinc-500">
              <Users className="h-10 w-10 mx-auto mb-3 text-zinc-300" />
              <p className="font-medium">No prospects yet</p>
              <p className="text-sm mb-4">
                Send an invite to get started.
              </p>
              <Button variant="gradient" onClick={openFreshInvite}>
                Invite a prospect
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all prospects"
                        className="h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer align-middle"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="text-zinc-500">Name</TableHead>
                    <TableHead className="text-zinc-500 hidden md:table-cell">Email</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Stage</TableHead>
                    <TableHead className="w-10 text-right">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prospects.map((p) => (
                    <TableRow
                      key={p.id}
                      className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      tabIndex={0}
                      aria-label={`View ${p.name || "prospect"}`}
                      onClick={() => openProspect(p)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          openProspect(p)
                        }
                      }}
                    >
                      <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${p.name || p.email}`}
                          className="h-4 w-4 rounded border-zinc-300 text-kf-red focus:ring-kf-red cursor-pointer align-middle"
                          checked={selectedIds.has(p.id)}
                          onChange={() => toggleSelect(p.id)}
                        />
                      </TableCell>
                      <TableCell className="whitespace-normal">
                        <div className="font-medium text-zinc-900">{p.name}</div>
                        <div className="text-xs text-zinc-500 md:hidden flex items-center gap-1 mt-0.5">
                          <Mail className="h-3 w-3 shrink-0" />
                          {p.email}
                        </div>
                        <div className="mt-2 flex flex-col gap-0.5 items-start sm:hidden">
                          <div className="flex flex-wrap items-center gap-1">
                            <FunnelStageBadge stage={p.stage} label={p.stageLabel} />
                            {p.selfRegistered && (
                              <Badge
                                variant="outline"
                                className="text-xs font-medium bg-purple-100 text-purple-800 border-purple-200"
                              >
                                Self-registered
                              </Badge>
                            )}
                          </div>
                          {p.stageTimestamp && (
                            <span className="text-xs text-zinc-500">
                              {formatRelative(p.stageTimestamp)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-zinc-600">
                        {p.email}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell whitespace-normal">
                        <div className="flex flex-col gap-0.5 items-start">
                          <div className="flex flex-wrap items-center gap-1">
                            <FunnelStageBadge stage={p.stage} label={p.stageLabel} />
                            {p.selfRegistered && (
                              <Badge
                                variant="outline"
                                className="text-xs font-medium bg-purple-100 text-purple-800 border-purple-200"
                              >
                                Self-registered
                              </Badge>
                            )}
                          </div>
                          {p.stageTimestamp && (
                            <span className="text-xs text-zinc-500">
                              {formatRelative(p.stageTimestamp)}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell
                        className="w-10 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-zinc-400 hover:text-red-600"
                          aria-label={`Remove ${p.name || "prospect"}`}
                          title="Remove from prospects"
                          onClick={() => setPendingRemoval([p])}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={(next) => { if (!next) setResult(null); setInviteOpen(next) }}
        generated={result}
        coachName={coachDisplayName || currentUser?.name}
      />

      <AlertDialog
        open={!!pendingRemoval}
        onOpenChange={(open) => { if (!open) setPendingRemoval(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {removeCount === 1
                ? `Remove ${pendingRemoval?.[0]?.name || "this prospect"}?`
                : `Remove ${removeCount} prospects?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {removeCount === 1 ? "This prospect" : "These prospects"} will drop off
              your list and their account{removeCount === 1 ? "" : "s"} will be deactivated.
              They won&rsquo;t be able to sign in until an admin reactivates them or you
              invite them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoval}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {removeCount === 1 ? "Remove prospect" : `Remove ${removeCount} prospects`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
